'use client';

import Link from 'next/link';

import { useTranslations } from '@/components/language-provider';

type PlanIds = 'free' | 'basic' | 'pro';

function CheckIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" {...props}>
      <path d="M4 10.5 8.5 15 16 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CrossIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" {...props}>
      <path d="M5 5 15 15" strokeLinecap="round" />
      <path d="M15 5 5 15" strokeLinecap="round" />
    </svg>
  );
}

export default function PlansPage() {
  const { messages } = useTranslations();
  const { subscriptionPlans: content } = messages;

  const planOrder = content.cards.map((plan) => plan.id as PlanIds);

  const renderCellValue = (value: string) => {
    if (value === 'check') {
      return (
        <span className="inline-flex items-center justify-center rounded-full bg-primary/15 px-2 py-1 text-xs font-semibold text-primary">
          <CheckIcon className="mr-1 size-3.5" />
          {content.table.legend.check}
        </span>
      );
    }
    if (value === 'cross') {
      return (
        <span className="inline-flex items-center justify-center rounded-full bg-muted/40 px-2 py-1 text-xs font-semibold text-muted-foreground">
          <CrossIcon className="mr-1 size-3.5" />
          {content.table.legend.cross}
        </span>
      );
    }
    return <span>{value}</span>;
  };

  return (
    <div className="space-y-16">
      <section className="grid gap-10 rounded-3xl border border-border/60 bg-surface/90 p-10 shadow-xl shadow-primary/10 backdrop-blur lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
        <div className="space-y-6">
          <span className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-muted/40 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <span className="size-2 rounded-full bg-primary" aria-hidden="true" />
            {content.hero.eyebrow}
          </span>
          <h1 className="text-balance text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
            {content.hero.title}
          </h1>
          <p className="text-lg text-muted-foreground">{content.hero.description}</p>
          <p className="text-sm font-medium text-muted-foreground/80">{content.hero.note}</p>
        </div>
        <div className="space-y-4 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/90 to-primary/70 p-6 text-primary-foreground shadow-lg shadow-primary/40">
          <h2 className="text-xl font-semibold">{content.cta.title}</h2>
          <p className="text-sm text-primary-foreground/90">{content.cta.description}</p>
          <div className="flex flex-wrap gap-3 pt-2">
            <Link
              href={content.cta.primaryHref}
              className="inline-flex items-center justify-center rounded-full bg-white px-5 py-2.5 text-sm font-semibold shadow-sm transition hover:-translate-y-0.5 hover:bg-primary-foreground/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-foreground/40"
              style={{ color: 'var(--color-primary)' }}
            >
              {content.cta.primaryLabel}
            </Link>
            <Link
              href={content.cta.secondaryHref}
              className="inline-flex items-center justify-center rounded-full border border-white/70 px-5 py-2.5 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/60"
            >
              {content.cta.secondaryLabel}
            </Link>
          </div>
        </div>
      </section>

      <section className="rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/15 via-surface to-surface p-6 shadow-lg shadow-primary/10">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-foreground">{content.courtIntelCallout.title}</h2>
            <p className="text-sm text-muted-foreground">{content.courtIntelCallout.description}</p>
            {'note' in content.courtIntelCallout ? (
              <p className="text-xs font-medium text-muted-foreground/90">{content.courtIntelCallout.note}</p>
            ) : null}
          </div>
          <Link
            href={content.courtIntelCallout.primaryHref}
            className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold btn-primary shadow-md shadow-primary/30 transition hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            {content.courtIntelCallout.primaryLabel}
          </Link>
        </div>
      </section>

      <section className="space-y-8">
        <div className="grid gap-6 lg:grid-cols-3">
          {content.cards.map((plan) => {
            const highlighted = 'highlighted' in plan && plan.highlighted;
            return (
            <div
              key={plan.id}
              className={`relative flex h-full flex-col gap-6 rounded-3xl border border-border/60 bg-surface p-8 shadow-lg shadow-primary/10 transition hover:-translate-y-1 hover:shadow-xl ${
                highlighted ? 'ring-2 ring-primary' : ''
              }`}
            >
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {plan.badge}
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-full bg-muted/40 px-3 py-1 text-xs font-semibold text-muted-foreground">
                    {plan.priceMonthly}
                  </span>
                </div>
                <h3 className="text-2xl font-semibold text-foreground">{plan.name}</h3>
                <p className="text-sm text-muted-foreground">{plan.description}</p>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground/70">
                  {plan.priceYearly}
                </p>
              </div>
              <ul className="space-y-3 text-sm text-muted-foreground">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <span className="mt-1 size-1.5 flex-shrink-0 rounded-full bg-primary" aria-hidden="true" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-auto">
                <Link
                  href="/auth/signup"
                  className={`inline-flex w-full items-center justify-center rounded-full px-5 py-3 text-sm font-semibold transition focus-visible:outline-2 focus-visible:outline-offset-2 ${
                    highlighted
                      ? 'bg-primary btn-primary shadow-lg shadow-primary/40 hover:-translate-y-0.5 hover:shadow-xl focus-visible:outline-ring'
                      : 'border border-border bg-surface text-foreground shadow-sm hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-ring'
                  }`}
                >
                  {plan.ctaLabel}
                </Link>
              </div>
            </div>
          );
          })}
        </div>
      </section>

      <section className="space-y-6 rounded-3xl border border-border/60 bg-surface/90 p-8 shadow-lg shadow-primary/10">
        <div className="space-y-3">
          <h2 className="text-2xl font-semibold text-foreground">{content.table.title}</h2>
          <p className="text-sm text-muted-foreground">{content.table.description}</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border/60 text-left text-sm text-muted-foreground">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-muted-foreground/80">
                <th scope="col" className="py-3 pr-4 font-semibold text-foreground">
                  {content.table.featureColumnLabel}
                </th>
                {planOrder.map((planId) => (
                  <th key={planId} scope="col" className="px-4 py-3 font-semibold text-foreground">
                    {content.table.planLabels[planId]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border/50">
              {content.table.rows.map((row) => (
                <tr key={row.label} className="hover:bg-muted/20">
                  <th scope="row" className="py-3 pr-4 text-sm font-medium text-foreground">
                    {row.label}
                  </th>
                  {planOrder.map((planId) => {
                    const value = row.values[planId] ?? '—';
                    return (
                      <td key={`${row.label}-${planId}`} className="px-4 py-3 text-sm">
                        {renderCellValue(value)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground">{content.footnote}</p>
      </section>
    </div>
  );
}
